<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import ContactImportsAPI from 'dashboard/api/contactImports';
import Button from 'dashboard/components-next/button/Button.vue';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const emit = defineEmits(['resultUpdated']);
const { t, locale } = useI18n();
const route = useRoute();
const panel = ref(null);
const records = ref([]);
const page = ref(1);
const total = ref(0);
const activeCount = ref(0);
const loading = ref(false);
const error = ref(false);
const downloadError = ref(false);
const downloadingId = ref(null);
let pollTimer;
let revision = 0;
let downloadRevision = 0;
let disposed = false;
const observedResults = new Map();
let resultVersion;

const statusKeys = {
  pending: 'CONTACTS_IMPORT_HISTORY.PENDING',
  processing: 'CONTACTS_IMPORT_HISTORY.PROCESSING',
  completed: 'CONTACTS_IMPORT_HISTORY.COMPLETED',
  failed: 'CONTACTS_IMPORT_HISTORY.FAILED',
};
const statusKey = item => {
  if (
    item.status === 'completed' &&
    item.total_records !== null &&
    item.total_records !== undefined &&
    item.processed_records !== item.total_records
  ) {
    return 'CONTACTS_IMPORT_HISTORY.COMPLETED_WITH_REJECTIONS';
  }
  return statusKeys[item.status] || 'CONTACTS_IMPORT_HISTORY.UNKNOWN';
};
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(toIntlLocale(locale.value), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : '';

const load = async (target = page.value, silent = false) => {
  if (disposed) return;
  clearTimeout(pollTimer);
  revision += 1;
  const request = revision;
  loading.value = !silent;
  error.value = false;
  try {
    const { data } = await ContactImportsAPI.get(target);
    if (request !== revision) return;
    let resultChanged = false;
    data.payload.forEach(item => {
      if (['pending', 'processing'].includes(item.status)) return;
      const signature = `${item.status}:${item.updated_at}`;
      if (observedResults.get(item.id) !== signature) {
        observedResults.set(item.id, signature);
        resultChanged = true;
      }
    });
    if (
      data.meta.result_version &&
      data.meta.result_version !== resultVersion
    ) {
      resultVersion = data.meta.result_version;
      resultChanged = true;
    }
    if (resultChanged) emit('resultUpdated');
    records.value = data.payload;
    page.value = data.meta.page;
    total.value = data.meta.count;
    activeCount.value = data.meta.active_count ?? 0;
  } catch {
    if (request === revision) error.value = true;
  } finally {
    if (request === revision) {
      loading.value = false;
      const active =
        activeCount.value > 0 ||
        records.value.some(item =>
          ['pending', 'processing'].includes(item.status)
        );
      if (panel.value?.open && active && !error.value) {
        pollTimer = setTimeout(() => load(page.value, true), 5000);
      }
    }
  }
};

const downloadFailedRecords = async item => {
  if (!item.has_failed_records || downloadingId.value || disposed) return;
  const accountId = String(route.params.accountId);
  downloadRevision += 1;
  const request = downloadRevision;
  const isCurrent = () =>
    !disposed &&
    request === downloadRevision &&
    accountId === String(route.params.accountId);
  downloadingId.value = item.id;
  downloadError.value = false;
  try {
    const response = await ContactImportsAPI.getFailedRecords(item.id);
    if (!isCurrent()) return;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contact-import-${item.id}-rejected.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    if (isCurrent()) downloadError.value = true;
  } finally {
    if (isCurrent()) downloadingId.value = null;
  }
};

const onToggle = event => {
  if (event.target.open) load(1);
  else {
    revision += 1;
    downloadRevision += 1;
    downloadingId.value = null;
    clearTimeout(pollTimer);
  }
};
const reveal = () => {
  if (panel.value?.open) load(1);
  else if (panel.value) panel.value.open = true;
};

defineExpose({ reveal, load });
onBeforeUnmount(() => {
  disposed = true;
  revision += 1;
  downloadRevision += 1;
  clearTimeout(pollTimer);
});
</script>

<template>
  <details
    ref="panel"
    data-testid="contact-import-history"
    class="mx-4 my-2 rounded-xl border border-n-weak bg-n-solid-1"
    @toggle="onToggle"
  >
    <summary class="min-h-11 cursor-pointer px-4 py-3 text-sm font-medium">
      {{ t('CONTACTS_IMPORT_HISTORY.TITLE') }}
    </summary>
    <div
      class="flex flex-col gap-3 border-t border-n-weak p-4"
      :aria-busy="loading"
    >
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONTACTS_IMPORT_HISTORY.ERROR') }}
      </p>
      <p v-else-if="loading" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_IMPORT_HISTORY.LOADING') }}
      </p>
      <template v-else>
        <p v-if="!records.length" class="m-0 text-sm text-n-slate-11">
          {{ t('CONTACTS_IMPORT_HISTORY.EMPTY') }}
        </p>
        <ul v-else class="m-0 flex list-none flex-col gap-3 p-0">
          <li
            v-for="item in records"
            :key="item.id"
            class="flex flex-wrap items-center justify-between gap-2 text-sm"
          >
            <span class="flex min-w-0 flex-col gap-1">
              <span>{{ t('CONTACTS_IMPORT_HISTORY.CONTACTS') }}</span>
              <span class="text-xs text-n-slate-11">{{
                formatDate(item.created_at)
              }}</span>
              <span
                v-if="
                  item.total_records !== null &&
                  item.total_records !== undefined
                "
                class="text-xs text-n-slate-11"
              >
                {{
                  t('CONTACTS_IMPORT_HISTORY.PROGRESS', {
                    processed: item.processed_records ?? 0,
                    total: item.total_records,
                  })
                }}
              </span>
            </span>
            <span>{{ t(statusKey(item)) }}</span>
            <Button
              v-if="item.has_failed_records"
              :label="t('CONTACTS_IMPORT_HISTORY.DOWNLOAD_FAILED_RECORDS')"
              variant="ghost"
              color="slate"
              class="!min-h-11 !rounded-xl"
              :disabled="downloadingId !== null"
              :is-loading="downloadingId === item.id"
              @click="downloadFailedRecords(item)"
            />
          </li>
        </ul>
      </template>
      <p v-if="downloadError" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONTACTS_IMPORT_HISTORY.DOWNLOAD_ERROR') }}
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          :label="t('CONTACTS_IMPORT_HISTORY.REFRESH')"
          variant="faded"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load()"
        />
        <Button
          v-if="page > 1"
          :label="t('CONTACTS_IMPORT_HISTORY.PREVIOUS')"
          variant="ghost"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load(page - 1)"
        />
        <Button
          v-if="page * 20 < total"
          :label="t('CONTACTS_IMPORT_HISTORY.NEXT')"
          variant="ghost"
          color="slate"
          class="!min-h-11 !rounded-xl"
          :disabled="loading"
          @click="load(page + 1)"
        />
      </div>
    </div>
  </details>
</template>
